import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { Box, Chip, Stack, Typography } from '@mui/material';
import React from 'react';

import CustomButton from '../../../components/button/ButtonComponent';
import { colors } from '../../../components/theme/theme';
import { useTranslation } from '../../../utils/translations';

export type OtpDeliveryMethod = 'email' | 'sms';

interface TwoFactorMethodPickerProps {
    maskedEmail: string;
    maskedPhone: string;
    isPhoneVerified: boolean;
    selected: OtpDeliveryMethod;
    onSelect: (method: OtpDeliveryMethod) => void;
    sending?: boolean;
    onSend: () => void;
    onVerifyMobile: () => void;
    onUpdatePhone?: () => void;
    onCancel: () => void;
}

const iconBoxSx = { width: 44, height: 44, borderRadius: 2, display: 'grid', placeItems: 'center', flexShrink: 0 };
const maskedSx = { color: 'text.secondary' };

export const TwoFactorMethodPicker: React.FC<TwoFactorMethodPickerProps> = ({
    maskedEmail,
    maskedPhone,
    isPhoneVerified,
    selected,
    onSelect,
    sending,
    onSend,
    onVerifyMobile,
    onUpdatePhone,
    onCancel,
}) => {
    const { t } = useTranslation();
    const emailSelected = selected === 'email';
    const smsSelected = selected === 'sms' && isPhoneVerified;

    const checkMark = (checked: boolean) =>
        checked ? (
            <CheckCircleIcon color="primary" />
        ) : (
            <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'grey.300' }} />
        );

    const activate = (method: OtpDeliveryMethod) => (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(method);
        }
    };

    return (
        <Stack alignItems="center" gap={2} width="100%">
            <Typography variant="h5" component="h1" textAlign="center">
                {t('authFlow.twoFactor.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
                {t('authFlow.twoFactor.subtitle')}
            </Typography>

            <Stack role="radiogroup" aria-label="Verification method" gap={1.5} width="100%">
                <Stack
                    role="radio"
                    aria-checked={emailSelected}
                    tabIndex={0}
                    onClick={() => onSelect('email')}
                    onKeyDown={activate('email')}
                    direction="row"
                    alignItems="center"
                    gap={2}
                    p={1.5}
                    sx={{ borderRadius: 3, bgcolor: emailSelected ? 'grey.100' : 'common.white', cursor: 'pointer' }}
                >
                    <Box
                        sx={{
                            ...iconBoxSx,
                            bgcolor: emailSelected ? 'primary.main' : 'grey.200',
                            color: emailSelected ? 'common.white' : 'text.secondary',
                        }}
                    >
                        <MarkunreadOutlinedIcon />
                    </Box>
                    <Box flex={1} minWidth={0}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="subtitle2">{t('authFlow.twoFactor.emailLabel')}</Typography>
                            <Chip size="small" label={t('authFlow.twoFactor.fastest')} />
                        </Stack>
                        <Typography variant="caption" sx={{ ...maskedSx, wordBreak: 'break-all' }}>
                            {maskedEmail}
                        </Typography>
                    </Box>
                    {checkMark(emailSelected)}
                </Stack>

                {maskedPhone && (
                    <Stack
                        role="radio"
                        aria-checked={smsSelected}
                        aria-disabled={!isPhoneVerified}
                        tabIndex={isPhoneVerified ? 0 : -1}
                        onClick={() => isPhoneVerified && onSelect('sms')}
                        onKeyDown={isPhoneVerified ? activate('sms') : undefined}
                        gap={1.5}
                        p={1.5}
                        sx={{
                            borderRadius: 3,
                            bgcolor: smsSelected ? 'grey.100' : 'common.white',
                            cursor: isPhoneVerified ? 'pointer' : 'default',
                            ...(!isPhoneVerified && { border: 1, borderColor: 'error.main' }),
                        }}
                    >
                        <Stack direction="row" alignItems="center" gap={2}>
                            <Box
                                sx={{
                                    ...iconBoxSx,
                                    bgcolor: smsSelected ? 'primary.main' : 'grey.200',
                                    color: smsSelected ? 'common.white' : 'text.secondary',
                                }}
                            >
                                <SmartphoneOutlinedIcon />
                            </Box>
                            <Box flex={1}>
                                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                    <Typography variant="subtitle2">{t('authFlow.twoFactor.smsLabel')}</Typography>
                                    {!isPhoneVerified && (
                                        <Chip
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            label={t('authFlow.twoFactor.smsNotVerified')}
                                        />
                                    )}
                                </Stack>
                                <Typography variant="caption" sx={maskedSx}>
                                    {maskedPhone}
                                </Typography>
                            </Box>
                            {isPhoneVerified && checkMark(smsSelected)}
                        </Stack>
                        {!isPhoneVerified && (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    {t('authFlow.twoFactor.smsHelper')}
                                </Typography>
                                <CustomButton
                                    variant="outlined"
                                    borderColor={colors.errorPreset[10]}
                                    textColor={colors.errorPreset[10]}
                                    startIcon={<VerifiedUserOutlinedIcon />}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onVerifyMobile();
                                    }}
                                >
                                    {`${t('authFlow.twoFactor.verifyMobile')} →`}
                                </CustomButton>
                            </>
                        )}
                    </Stack>
                )}
            </Stack>

            <CustomButton variant="contained" disabled={sending} onClick={onSend}>
                {`${t('authFlow.twoFactor.send')} →`}
            </CustomButton>
            {onUpdatePhone && (
                <CustomButton variant="text" disabled={sending} onClick={onUpdatePhone}>
                    {t('authFlow.updatePhone.link')}
                </CustomButton>
            )}
            <CustomButton variant="text" disabled={sending} onClick={onCancel}>
                {t('authFlow.twoFactor.cancel')}
            </CustomButton>
        </Stack>
    );
};
